import { supabase } from '../supabase';

export const XP_REWARDS = {
  // Profile Actions
  PROFILE_COMPLETE: 100,
  AVATAR_UPLOAD: 20,
  SKILLS_ADDED: 30,

  // Resume Actions
  RESUME_UPLOAD: 50,
  RESUME_COMPLETE: 75,
  RESUME_UPDATED: 15,

  // Job Application Actions
  APPLICATION_SENT: 25,
  APPLICATION_VIEWED: 10,
  INTERVIEW_SCHEDULED: 100,
  JOB_OFFER_RECEIVED: 500,

  // Daily Actions
  DAILY_LOGIN: 5,
  STREAK_BONUS_7_DAYS: 50,
  STREAK_BONUS_30_DAYS: 200,

  // Engagement
  JOB_SAVED: 10,
  MESSAGE_SENT: 5,
  PROFILE_VIEWED: 2,

  // Learning & Development
  COURSE_COMPLETED: 100,
  ASSESSMENT_PASSED: 75,
  CERTIFICATE_EARNED: 150,

  // Social
  REFERRAL_SENT: 15,
  REFERRAL_SIGNUP: 100,
  REVIEW_SUBMITTED: 25,

  // Badges & Quests
  BADGE_EARNED: 75,
  QUEST_COMPLETED: 50,
} as const;

export type XPSource = keyof typeof XP_REWARDS;

interface AwardXPResult {
  newXp: number;
  newLevel: number;
  leveledUp: boolean;
  xpEarned: number;
}

/**
 * Award XP to a user for a specific action
 */
export async function awardXP(
  userId: string,
  source: XPSource,
  description?: string
): Promise<AwardXPResult> {
  const xpAmount = XP_REWARDS[source];

  try {
    // Call database function to award XP and handle level up
    const { data, error } = await supabase.rpc('award_xp', {
      p_user_id: userId,
      p_xp_amount: xpAmount,
      p_source: source,
      p_description: description || `Earned ${xpAmount} XP for ${source}`,
    });

    if (error) throw error;

    const result = data[0];
    return {
      newXp: result.new_xp,
      newLevel: result.new_level,
      leveledUp: result.leveled_up,
      xpEarned: xpAmount,
    };
  } catch (error) {
    console.error('Error awarding XP:', error);
    throw error;
  }
}

/**
 * Get XP required for next level
 */
export function getXPForLevel(level: number): number {
  // Exponential growth: level 1 = 0, level 2 = 100, level 3 = 250, etc.
  const xpTable: { [key: number]: number } = {
    1: 0,
    2: 100,
    3: 250,
    4: 500,
    5: 1000,
    6: 1500,
    7: 2250,
    8: 3000,
    9: 4000,
    10: 5000,
  };

  return xpTable[level] || Math.floor(Math.pow(level - 1, 2) * 100);
}

/**
 * Calculate level from total XP
 */
export function calculateLevel(xp: number): number {
  let level = 1;
  while (getXPForLevel(level + 1) <= xp) {
    level++;
  }
  return level;
}

/**
 * Get progress percentage to next level
 */
export function getLevelProgress(currentXP: number, currentLevel: number): number {
  const currentLevelXP = getXPForLevel(currentLevel);
  const nextLevelXP = getXPForLevel(currentLevel + 1);
  const xpInCurrentLevel = currentXP - currentLevelXP;
  const xpNeededForNextLevel = nextLevelXP - currentLevelXP;

  return Math.min(100, Math.floor((xpInCurrentLevel / xpNeededForNextLevel) * 100));
}

/**
 * Get user's XP history
 */
export async function getXPHistory(userId: string, limit: number = 50) {
  const { data, error } = await supabase
    .from('user_xp_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching XP history:', error);
    return [];
  }

  return data;
}

/**
 * Check and award streak bonus
 */
export async function checkStreakBonus(userId: string, streakDays: number): Promise<void> {
  if (streakDays === 7) {
    await awardXP(userId, 'STREAK_BONUS_7_DAYS', '7-day login streak bonus!');
  } else if (streakDays === 30) {
    await awardXP(userId, 'STREAK_BONUS_30_DAYS', '30-day login streak bonus!');
  }
}

/**
 * Award badge and its XP reward
 */
export async function awardBadge(userId: string, badgeId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('award_badge', {
      p_user_id: userId,
      p_badge_id: badgeId,
    });

    if (error) throw error;
    return data; // Returns true if badge was awarded, false if already earned
  } catch (error) {
    console.error('Error awarding badge:', error);
    return false;
  }
}

/**
 * Update quest progress
 */
export async function updateQuestProgress(
  userId: string,
  questId: string,
  increment: number = 1
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('update_quest_progress', {
      p_user_id: userId,
      p_quest_id: questId,
      p_increment: increment,
    });

    if (error) throw error;
    return data; // Returns true if quest was completed
  } catch (error) {
    console.error('Error updating quest progress:', error);
    return false;
  }
}

/**
 * Get user's active quests
 */
export async function getUserQuests(userId: string) {
  const { data, error } = await supabase
    .from('user_quests')
    .select(`
      *,
      quest:quests(*)
    `)
    .eq('user_id', userId)
    .eq('completed', false)
    .order('started_at', { ascending: false });

  if (error) {
    console.error('Error fetching user quests:', error);
    return [];
  }

  return data;
}

/**
 * Get all available quests
 */
export async function getAvailableQuests() {
  const { data, error } = await supabase
    .from('quests')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching quests:', error);
    return [];
  }

  return data;
}

/**
 * Get user's earned badges
 */
export async function getUserBadges(userId: string) {
  const { data, error } = await supabase
    .from('user_badges')
    .select(`
      *,
      badge:skill_badges(*)
    `)
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });

  if (error) {
    console.error('Error fetching user badges:', error);
    return [];
  }

  return data;
}

/**
 * Get leaderboard data
 */
export async function getLeaderboard(
  period: 'weekly' | 'monthly' | 'all_time',
  metric: 'xp' | 'applications' | 'badges',
  limit: number = 100
) {
  const { data, error } = await supabase
    .from('leaderboards')
    .select(`
      *,
      profile:profiles(full_name, avatar_url)
    `)
    .eq('period', period)
    .eq('metric', metric)
    .order('rank', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }

  return data;
}

/**
 * Get user's rank on leaderboard
 */
export async function getUserRank(
  userId: string,
  period: 'weekly' | 'monthly' | 'all_time',
  metric: 'xp' | 'applications' | 'badges'
) {
  const { data, error } = await supabase
    .from('leaderboards')
    .select('rank, score')
    .eq('user_id', userId)
    .eq('period', period)
    .eq('metric', metric)
    .single();

  if (error) {
    console.error('Error fetching user rank:', error);
    return null;
  }

  return data;
}
